import { useSuspenseQuery, gql } from '@apollo/client';
import {
  useOrderBy,
  usePageIndex,
  usePaginatingSortingTable,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
} from 'components/table';
import { Tag } from 'components/tag';
import {
  GetDosageSchedulesQuery,
  GetDosageSchedulesQueryVariables,
} from 'graphql/types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { Column } from 'react-table';
import { getProblemTypeColor, getProblemTypeEmoji } from 'utils/misc';
import { buildRoute } from 'utils/routes';

const columns: Column<
  NonNullable<GetDosageSchedulesQuery['dosageSchedules']>[number]
>[] = [
  {
    Header: 'Name',
    accessor: 'displayName',
    Cell: (c) => <div>{c.value}</div>,
    className: 'w-1/7',
  },
  {
    Header: 'Medications',
    accessor: 'doses',
    Cell: (c) => {
      const doses = c.row.original.doses;
      const delimitedMedicineTradeNames = [
        ...new Set(doses.map((dose) => dose.dose?.medicineTradeName)),
      ].join(', ');
      return <div>{delimitedMedicineTradeNames}</div>;
    },
    className: 'w-1/7',
    disableSortBy: true,
  },
  {
    Header: 'Linked Sequences',
    accessor: 'sequences',
    Cell: (c) => {
      const sequences = c.row.original.sequences;
      if (sequences?.length === 0) {
        return <></>;
      }
      return (
        <div>
          {sequences?.map((sequence) => {
            return <div key={sequence?.id}>{sequence.internalName}</div>;
          })}
        </div>
      );
    },
    className: 'w-1/7',
    disableSortBy: true,
  },
  {
    Header: 'Problem Types',
    accessor: 'maxDaysSinceDoseBeforeIntervention',
    Cell: (c) => {
      const sequences = c.row.original.sequences;

      if (sequences?.length === 0) {
        return <></>;
      }

      const uniqueProblemTypes = [
        ...new Set(
          sequences?.flatMap((sequence) => sequence?.problemTypes || []),
        ),
      ];
      return (
        <div>
          {uniqueProblemTypes?.map((problemType) => {
            return (
              <Tag
                key={problemType}
                size="small"
                color={getProblemTypeColor(problemType)}
              >
                <span role="img" className="pr-1">
                  {getProblemTypeEmoji(problemType)}
                </span>
                {problemType}
              </Tag>
            );
          })}
        </div>
      );
    },
    className: 'w-1/7',
    disableSortBy: true,
  },
];

const TABLE_PAGE_SIZE = 30;

const DosageScheduleTable = (): React.ReactElement => {
  const orderBy = useOrderBy({ defaultValue: 'createdAt_desc' });
  const pageIndex = usePageIndex();

  const history = useHistory();
  const { data, error } = useSuspenseQuery<
    GetDosageSchedulesQuery,
    GetDosageSchedulesQueryVariables
  >(gql`
    query GetDosageSchedules {
      dosageSchedules {
        displayName
        id
        internalName
        maxDaysSinceDoseBeforeIntervention
        minSafeDoseIntervalDays
        doses {
          id
          dose {
            id
            medicineTradeName
          }
          variant {
            id
            name
          }
        }
        sequences {
          id
          internalName
          status
          problemTypes
        }
      }
    }
  `);
  const total = data?.dosageSchedules ? data.dosageSchedules.length : 0;

  const pageNumber = Math.ceil(total / TABLE_PAGE_SIZE);

  const dosageSchedules = data?.dosageSchedules ?? [];

  const tableInstance = usePaginatingSortingTable({
    columns,
    data: dosageSchedules,
    pageNumber,
    orderBy,
    pageIndex,
  });

  if (error) {
    return <div>Error. Please contact #help-technology.</div>;
  }

  return (
    <section>
      <div className="pt-2 w-full">
        <Table tableInstance={tableInstance}>
          <TableHead />
          <TableBody>
            <React.Fragment>
              {tableInstance.page?.map((row) => {
                tableInstance.prepareRow(row);
                return (
                  <TableRow row={row} key={row.id}>
                    {row.cells.map((cell) => (
                      <TableCell
                        key={`${cell.row.original.id}-${cell.column.id}`}
                        cell={cell}
                        onClick={(): void => {
                          history.push(
                            buildRoute.dosageSchedule(cell.row.original.id),
                          );
                        }}
                      />
                    ))}
                  </TableRow>
                );
              })}
            </React.Fragment>
          </TableBody>
        </Table>
        {!data.dosageSchedules ||
          (data.dosageSchedules.length === 0 && (
            <div className="flex justify-center w-full ">
              <div className="p-3 text-sm">No dosage schedules found</div>
            </div>
          ))}
      </div>
      <div>
        <Pagination total={total} tableInstance={tableInstance} />
      </div>
    </section>
  );
};

export default DosageScheduleTable;
